import { useEffect, useCallback, useMemo } from 'react'

import { usePreviewMode } from 'Pods/ItemPage/context/PreviewModeContext'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

function parseToInteger(value) {
  const parsedValue = parseInt(value, 10)
  return Number.isNaN(parsedValue) ? undefined : parsedValue
}

function resolveProductPricing({ cart, attributes, isCurrentUser }) {
  const { price: originalPrice, saleOffDiscount, isSaleOffApplicable, isCLDApplicable } = attributes
  const { isSaleOff, isCLDDisabled, value, applicableCldValue, creditsAvailable } = cart

  const calculateDiscount = (discountedPrice) => ({
    price: discountedPrice,
    originalPrice,
    discount: 1.0 - discountedPrice / originalPrice,
    discountAmount: originalPrice - discountedPrice,
  })

  if (isSaleOff && isSaleOffApplicable && saleOffDiscount !== 0) {
    const discountedPrice = (originalPrice * (100 - saleOffDiscount)) / 100.0
    return { ...calculateDiscount(discountedPrice), ribbon: 'saleOff' }
  }

  if (!isCLDDisabled && isCLDApplicable) {
    const isCartSizeSufficient = value + originalPrice >= applicableCldValue

    if (isCurrentUser && isCartSizeSufficient) {
      const discountedPrice = Math.max(originalPrice - creditsAvailable, originalPrice * 0.7)
      return { ...calculateDiscount(discountedPrice), ribbon: 'CLD' }
    }

    return {
      price: originalPrice,
      originalPrice,
      discount: 0,
      discountAmount: 0,
      ribbon: 'CLD',
    }
  }

  return {
    price: originalPrice,
    originalPrice,
    discount: 0,
  }
}

function prepareItemData(itemAttributes, pageConfig) {
  if (!pageConfig?.cart) {
    return null
  }

  const productPricing = resolveProductPricing({
    cart: pageConfig.cart,
    attributes: itemAttributes,
    isCurrentUser: pageConfig.isCurrentUser,
  })

  let discount = 0
  let price

  if (itemAttributes.searchingFree && productPricing.price > 0) {
    price = itemAttributes.price.toFixed(2)
  } else if (['saleOff', 'CLD'].includes(productPricing.ribbon) && productPricing.discount > 0) {
    discount = (productPricing.originalPrice - productPricing.price).toFixed(2)
    price = productPricing.price.toFixed(2)
  } else {
    price = itemAttributes.price.toFixed(2)
  }

  return {
    item_id: String(itemAttributes.id),
    item_name: itemAttributes.title,
    currency: 'USD',
    index: parseToInteger(itemAttributes.index),
    item_brand: itemAttributes.authorUsername,
    item_category: itemAttributes.category,
    item_category2: itemAttributes.subcategory,
    item_category3: itemAttributes.itemCategory3,
    item_category4: itemAttributes.itemCategory4,
    item_category5: itemAttributes.itemCategory5,
    item_list_id: itemAttributes.itemListId || itemAttributes.searchQuery,
    item_list_name: itemAttributes.itemListName || itemAttributes.searchQuery,
    price: parseFloat(price),
    value: parseFloat((price - discount).toFixed(2)),
    quantity: 1,
    discount: parseFloat(discount) || 0,
    is_subscription: !!itemAttributes.subscriptionSubscribed,
  }
}

// Global tracking state (persists across component remounts)
const trackingState = {
  viewItemTracked: false,
}

const useGA4ItemTracking = () => {
  const pageConfig = usePageConfig()
  const { isPreview } = usePreviewMode()
  const productId = useMemo(() => pageConfig?.product?.id, [pageConfig?.product?.id])

  useEffect(() => {
    // Skip tracking in preview mode
    if (isPreview) {
      return
    }

    // Early exit if missing required data
    if (!window.dataLayer || !productId || !pageConfig?.product || !pageConfig?.cart) {
      return
    }

    // Check global tracking state
    if (trackingState.viewItemTracked) {
      return
    }

    const item = prepareItemData(pageConfig.product, pageConfig)
    if (!item) {
      return
    }

    // Mark as tracked globally BEFORE pushing to prevent race conditions
    trackingState.viewItemTracked = true

    window.dataLayer.push({
      event: 'view_item',
      event_source: 'new_item_page',
      currency: 'USD',
      value: item.value,
      ecommerce: {
        items: [item],
      },
    })
  }, [isPreview, productId, pageConfig?.cart])

  const trackAddToCart = useCallback(() => {
    // Skip tracking in preview mode
    if (isPreview) {
      return
    }

    if (!window.dataLayer || !pageConfig?.product) {
      return
    }

    const item = prepareItemData(pageConfig.product, pageConfig)
    if (!item) {
      return
    }

    window.dataLayer.push({
      event: 'add_to_cart',
      user_id: pageConfig?.currentUserId || '',
      event_source: 'new_item_page',
      ecommerce: {
        currency: 'USD',
        value: item.price,
        items: [item],
      },
      _clear: true,
    })
  }, [isPreview, pageConfig])

  const trackBuyNow = useCallback(() => {
    if (isPreview) {
      return
    }

    if (!window.dataLayer || !pageConfig?.product) {
      return
    }

    const item = prepareItemData(pageConfig.product, pageConfig)
    if (!item) {
      return
    }

    window.dataLayer.push({
      event: 'buy_now_cta_click',
      user_id: pageConfig?.currentUserId || '',
      event_source: 'new_item_page',
      ecommerce: {
        currency: 'USD',
        value: item.price,
        items: [item],
      },
      _clear: true,
    })
  }, [isPreview, pageConfig])

  const trackAddToWishlist = useCallback(() => {
    // Skip tracking in preview mode
    if (isPreview) {
      return
    }

    if (!window.dataLayer || !pageConfig?.product) {
      return
    }

    const item = prepareItemData(pageConfig.product, pageConfig)
    if (!item) {
      return
    }

    window.dataLayer.push({
      event: 'add_to_wishlist',
      user_id: pageConfig?.currentUserId || '',
      event_source: 'new_item_page',
      ecommerce: {
        currency: 'USD',
        value: item.price,
        items: [item],
      },
    })
  }, [isPreview, pageConfig])

  return {
    trackAddToCart,
    trackBuyNow,
    trackAddToWishlist,
  }
}

export default useGA4ItemTracking
